import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";

const moneyFormat = (n) =>
  n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const Emoji = ({ children, isMobile }) => (
  <div className={`${isMobile ? "w-12 h-12 xs:w-14 xs:h-14 sm:w-18 sm:h-18" : "w-24 h-24"} flex items-center justify-center select-none`}>
    <span className={`${isMobile ? "text-3xl xs:text-4xl sm:text-5xl" : "text-[72px]"} leading-none`}>{children}</span>
  </div>
);

const Card = ({
  name,
  price,
  qty = 0, // Default value to prevent undefined
  onBuy,
  onSell,
  onQtyChange,
  icon,
  canAfford,
  isMobile,
}) => {
  // Ensure qty is never undefined or null
  const safeQty = qty || 0;
  const [displayQty, setDisplayQty] = useState(safeQty === 0 ? "0" : safeQty.toString());

  useEffect(() => {
    const safeQtyValue = qty || 0;
    setDisplayQty(safeQtyValue === 0 ? "0" : safeQtyValue.toString());
  }, [qty]);

  // Red only when qty > 0; disabled gray otherwise
  const sellBtnClass =
    safeQty === 0
      ? "bg-gray-200 text-black-400 cursor-not-allowed"
      : isMobile
        ? "bg-red-500 text-white hover:bg-red-600 active:scale-[0.95] transition-all duration-150"
        : "bg-red-500 text-white hover:bg-red-600 active:scale-[0.98]";

  // Buy button styling based on affordability
  const buyBtnClass = canAfford
    ? isMobile
      ? "bg-teal-500 text-white hover:bg-teal-600 active:scale-[0.95] transition-all duration-150"
      : "bg-teal-500 text-white hover:bg-teal-600 active:scale-[0.98]"
    : "bg-gray-200 text-gray-400 cursor-not-allowed";

  const handleQtyChange = (e) => {
    let value = e.target.value;

    if (displayQty === "0" && value.length > 1 && value.startsWith("0")) {
      value = value.slice(1); // Remove the leading "0"
    }
    // Update display immediately
    setDisplayQty(value);

    // If input is empty, set qty to 0
    if (value === "") {
      onQtyChange(0);
      return;
    }

    // Remove leading zeros and convert to number
    value = value.replace(/^0+/, "") || "0";
    const numValue = parseInt(value) || 0;

    if (numValue >= 0) {
      onQtyChange(numValue);
    }
  };

  const handleQtyFocus = () => {
    if (safeQty === 0) {
      setDisplayQty("0");
    }
  };

  const handleQtyBlur = () => {
    if (displayQty === "" && safeQty === 0) {
      setDisplayQty("0");
    }
  };

  if (isMobile) {
    // Mobile design with larger icons
    return (
      <div className="bg-white rounded-lg xs:rounded-xl border border-gray-200 shadow-sm p-2 xs:p-3 sm:p-4 flex flex-col items-center text-center min-h-[200px] xs:min-h-[220px] sm:min-h-[240px]">
        <div className="mb-2 xs:mb-3 sm:mb-3">{icon}</div>

        <h3 className="text-xs xs:text-sm sm:text-base font-semibold text-gray-900 mb-1 leading-tight text-center px-1">{name}</h3>
        <div className="text-teal-600 font-semibold mb-2 xs:mb-2 sm:mb-3 text-xs xs:text-xs sm:text-sm">
          ${price.toLocaleString()}
        </div>

        <div className="flex items-center gap-1 xs:gap-1 sm:gap-2 w-full mt-auto">
          <button
            onClick={onSell}
            disabled={safeQty === 0}
            className={`flex-1 px-1 xs:px-2 sm:px-2 py-1.5 xs:py-1.5 sm:py-2 rounded-md font-semibold transition text-xs xs:text-xs sm:text-sm min-h-[32px] xs:min-h-[36px] sm:min-h-[40px] ${sellBtnClass}`}
          >
            Sell
          </button>

          <input
            type="number"
            value={displayQty}
            onChange={handleQtyChange}
            onFocus={handleQtyFocus}
            onBlur={handleQtyBlur}
            min="0"
            placeholder=""
            className="w-8 xs:w-10 sm:w-12 text-center border border-gray-300 rounded-md py-1.5 xs:py-1.5 sm:py-2 bg-white text-xs xs:text-xs sm:text-sm min-h-[32px] xs:min-h-[36px] sm:min-h-[40px]"
          />

          <button
            onClick={onBuy}
            disabled={!canAfford}
            className={`flex-1 px-1 xs:px-2 sm:px-2 py-1.5 xs:py-1.5 sm:py-2 rounded-md font-semibold transition text-xs xs:text-xs sm:text-sm min-h-[32px] xs:min-h-[36px] sm:min-h-[40px] ${buyBtnClass}`}
          >
            Buy
          </button>
        </div>
      </div>
    );
  } else {
    // Desktop design (original)
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col items-center text-center">
        <div className="mb-3">{icon}</div>

        <h3 className="text-2xl font-semibold text-gray-900 mb-1">{name}</h3>
        <div className="text-teal-600 font-semibold mb-4">
          ${price.toLocaleString()}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onSell}
            disabled={safeQty === 0}
            className={`px-5 py-2 rounded-md font-semibold transition ${sellBtnClass}`}
          >
            Sell
          </button>

          <input
            type="number"
            value={displayQty}
            onChange={handleQtyChange}
            onFocus={handleQtyFocus}
            onBlur={handleQtyBlur}
            min="0"
            placeholder=""
            className="w-16 text-center border border-gray-300 rounded-md py-2 bg-white"
          />

          <button
            onClick={onBuy}
            disabled={!canAfford}
            className={`px-5 py-2 rounded-md font-semibold transition ${buyBtnClass}`}
          >
            Buy
          </button>
        </div>
      </div>
    );
  }
};

// Custom hook for animated money counter
const useAnimatedMoney = (targetAmount, duration = 1000) => {
  const [displayAmount, setDisplayAmount] = useState(targetAmount);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (displayAmount === targetAmount) return;

    setIsAnimating(true);
    const startAmount = displayAmount;
    const difference = targetAmount - startAmount;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use easeOutQuart for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentAmount = Math.round(startAmount + difference * easeOutQuart);

      setDisplayAmount(currentAmount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayAmount(targetAmount);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [targetAmount, duration]);

  return { displayAmount, isAnimating };
};

export default function BuySellPage() {
  // Banner value
  const START = 100_000_000_000;

  // Device detection
  const [isMobile, setIsMobile] = useState(false);

  // Check device type on mount and resize
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024); // lg breakpoint
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // SPORTS-ONLY PRODUCT CATALOG - SORTED BY PRICE (LOW TO HIGH)
  const items = useMemo(() => {
    const sportsItems = [
      // BALLS & BASIC SPORTS EQUIPMENT ($5-$35)
      { id: "pingpongball", name: "Ping Pong Balls", price: 5, icon: <Emoji isMobile={isMobile}>🏓</Emoji> },
      { id: "tennisball", name: "Tennis Balls", price: 8, icon: <Emoji isMobile={isMobile}>🎾</Emoji> },
      { id: "puck", name: "Hockey Puck", price: 8, icon: <Emoji isMobile={isMobile}>🏒</Emoji> },
      { id: "poolnoodle", name: "Pool Noodles", price: 8, icon: <Emoji isMobile={isMobile}>🏊</Emoji> },
      { id: "swimcap", name: "Swimming Cap", price: 10, icon: <Emoji isMobile={isMobile}>🏊</Emoji> },
      { id: "golftees", name: "Golf Tees", price: 10, icon: <Emoji isMobile={isMobile}>⛳</Emoji> },
      { id: "baseball", name: "Baseball", price: 12, icon: <Emoji isMobile={isMobile}>⚾</Emoji> },
      { id: "shuttlecock", name: "Shuttlecocks", price: 12, icon: <Emoji isMobile={isMobile}>🏸</Emoji> },
      { id: "sweatband", name: "Sweatbands", price: 12, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "cricketball", name: "Cricket Ball", price: 15, icon: <Emoji isMobile={isMobile}>🔴</Emoji> },
      { id: "golfball", name: "Golf Balls", price: 15, icon: <Emoji isMobile={isMobile}>⛳</Emoji> },
      { id: "frisbee", name: "Frisbee", price: 15, icon: <Emoji isMobile={isMobile}>🥏</Emoji> },
      { id: "whistle", name: "Referee Whistle", price: 15, icon: <Emoji isMobile={isMobile}>🎺</Emoji> },
      { id: "sportssocks", name: "Sports Socks", price: 15, icon: <Emoji isMobile={isMobile}>🧦</Emoji> },
      { id: "mouthguard", name: "Mouthguard", price: 15, icon: <Emoji isMobile={isMobile}>🦷</Emoji> },
      { id: "soccer", name: "Soccer Ball", price: 20, icon: <Emoji isMobile={isMobile}>⚽</Emoji> },
      { id: "kickboard", name: "Kickboard", price: 20, icon: <Emoji isMobile={isMobile}>🏊</Emoji> },
      { id: "waterbottle", name: "Sports Water Bottle", price: 20, icon: <Emoji isMobile={isMobile}>🍼</Emoji> },
      { id: "ballpump", name: "Ball Pump", price: 20, icon: <Emoji isMobile={isMobile}>⚽</Emoji> },
      { id: "visor", name: "Sports Visor", price: 20, icon: <Emoji isMobile={isMobile}>🧢</Emoji> },
      { id: "volleyball", name: "Volleyball", price: 25, icon: <Emoji isMobile={isMobile}>🏐</Emoji> },
      { id: "boomerang", name: "Boomerang", price: 25, icon: <Emoji isMobile={isMobile}>🪃</Emoji> },
      { id: "goggles", name: "Swimming Goggles", price: 25, icon: <Emoji isMobile={isMobile}>🥽</Emoji> },
      { id: "towel", name: "Sports Towel", price: 25, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "cones", name: "Training Cones", price: 25, icon: <Emoji isMobile={isMobile}>🔸</Emoji> },
      { id: "cap", name: "Sports Cap", price: 25, icon: <Emoji isMobile={isMobile}>🧢</Emoji> },
      { id: "ttnet", name: "Table Tennis Net", price: 25, icon: <Emoji isMobile={isMobile}>🏓</Emoji> },
      { id: "football", name: "American Football", price: 30, icon: <Emoji isMobile={isMobile}>🏈</Emoji> },
      { id: "markers", name: "Field Markers", price: 30, icon: <Emoji isMobile={isMobile}>🔶</Emoji> },
      { id: "elbowpad", name: "Elbow Pads", price: 30, icon: <Emoji isMobile={isMobile}>💪</Emoji> },
      { id: "resistance", name: "Resistance Bands", price: 30, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "stopwatch", name: "Stopwatch", price: 30, icon: <Emoji isMobile={isMobile}>⏱️</Emoji> },
      { id: "rugbyball", name: "Rugby Ball", price: 35, icon: <Emoji isMobile={isMobile}>🏉</Emoji> },
      { id: "shinpads", name: "Shin Guards", price: 35, icon: <Emoji isMobile={isMobile}>🦵</Emoji> },
      { id: "shorts", name: "Sports Shorts", price: 35, icon: <Emoji isMobile={isMobile}>🩳</Emoji> },
      { id: "foamroller", name: "Foam Roller", price: 35, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "timer", name: "Sports Timer", price: 35, icon: <Emoji isMobile={isMobile}>⏰</Emoji> },

      // SPORTS GEAR & EQUIPMENT ($40-$120)
      { id: "homeplate", name: "Home Plate", price: 40, icon: <Emoji isMobile={isMobile}>⚾</Emoji> },
      { id: "kneepad", name: "Knee Pads", price: 40, icon: <Emoji isMobile={isMobile}>🦵</Emoji> },
      { id: "yogamat", name: "Yoga Mat", price: 40, icon: <Emoji isMobile={isMobile}>🧘</Emoji> },
      { id: "tabletennis", name: "Table Tennis Paddle", price: 40, icon: <Emoji isMobile={isMobile}>🏓</Emoji> },
      { id: "boxing", name: "Boxing Gloves", price: 45, icon: <Emoji isMobile={isMobile}>🥊</Emoji> },
      { id: "sportsbra", name: "Sports Bra", price: 45, icon: <Emoji isMobile={isMobile}>👙</Emoji> },
      { id: "golfumbrella", name: "Golf Umbrella", price: 45, icon: <Emoji isMobile={isMobile}>☂️</Emoji> },
      {
        id: "bat",
        name: "Baseball Bat",
        price: 50,
        icon: (
          <div className={`${isMobile ? "w-12 h-12 xs:w-14 xs:h-14 sm:w-18 sm:h-18" : "w-24 h-24"} flex items-center justify-center`}>
            <svg viewBox="0 0 120 120" className={`${isMobile ? "w-8 h-8 xs:w-10 xs:h-10 sm:w-14 sm:h-14" : "w-[90px] h-[90px]"}`}>
              <rect x="55" y="20" width="12" height="70" rx="6" fill="#b07d42" />
              <rect x="55" y="80" width="12" height="20" rx="6" fill="#d29b62" />
              <circle cx="88" cy="55" r="10" fill="#ffffff" stroke="#222" strokeWidth="3" />
              <path d="M88 47 a8 8 0 0 1 0 16" fill="none" stroke="#f44336" strokeWidth="3" />
            </svg>
          </div>
        ),
      },
      { id: "cornerflag", name: "Corner Flags", price: 50, icon: <Emoji isMobile={isMobile}>🚩</Emoji> },
      { id: "medicineball", name: "Medicine Ball", price: 50, icon: <Emoji isMobile={isMobile}>⚽</Emoji> },
      { id: "protein", name: "Protein Powder", price: 50, icon: <Emoji isMobile={isMobile}>🥤</Emoji> },
      { id: "badmintonnet", name: "Badminton Net", price: 60, icon: <Emoji isMobile={isMobile}>🏸</Emoji> },
      { id: "karategi", name: "Karate Uniform", price: 60, icon: <Emoji isMobile={isMobile}>🥋</Emoji> },
      { id: "cricketgloves", name: "Cricket Gloves", price: 60, icon: <Emoji isMobile={isMobile}>🧤</Emoji> },
      { id: "kettlebell", name: "Kettlebell", price: 60, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "dartboard", name: "Dartboard", price: 60, icon: <Emoji isMobile={isMobile}>🎯</Emoji> },
      { id: "sportsbag", name: "Sports Bag", price: 60, icon: <Emoji isMobile={isMobile}>🎒</Emoji> },
      { id: "compressiongear", name: "Compression Wear", price: 60, icon: <Emoji isMobile={isMobile}>👕</Emoji> },
      { id: "basketball", name: "Basketball", price: 60, icon: <Emoji isMobile={isMobile}>🏀</Emoji> },
      { id: "headguard", name: "Boxing Headguard", price: 70, icon: <Emoji isMobile={isMobile}>🥊</Emoji> },
      { id: "hockey", name: "Hockey Stick", price: 75, icon: <Emoji isMobile={isMobile}>🏒</Emoji> },
      { id: "shotput", name: "Shot Put", price: 80, icon: <Emoji isMobile={isMobile}>⚫</Emoji> },
      { id: "wickets", name: "Cricket Wickets", price: 80, icon: <Emoji isMobile={isMobile}>🏏</Emoji> },
      { id: "judogi", name: "Judo Uniform", price: 80, icon: <Emoji isMobile={isMobile}>🥋</Emoji> },
      { id: "badminton", name: "Badminton Racket", price: 80, icon: <Emoji isMobile={isMobile}>🏸</Emoji> },
      { id: "baseballglove", name: "Baseball Glove", price: 80, icon: <Emoji isMobile={isMobile}>🧤</Emoji> },
      { id: "pullupbar", name: "Pull-up Bar", price: 80, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "skateboard", name: "Skateboard", price: 80, icon: <Emoji isMobile={isMobile}>🛹</Emoji> },
      { id: "poolballs", name: "Pool Ball Set", price: 80, icon: <Emoji isMobile={isMobile}>🎱</Emoji> },
      { id: "jersey", name: "Sports Jersey", price: 80, icon: <Emoji isMobile={isMobile}>👕</Emoji> },
      { id: "helmet", name: "Sports Helmet", price: 80, icon: <Emoji isMobile={isMobile}>⛑️</Emoji> },
      { id: "lifejacket", name: "Life Jacket", price: 80, icon: <Emoji isMobile={isMobile}>🦺</Emoji> },
      { id: "skisticks", name: "Ski Poles", price: 80, icon: <Emoji isMobile={isMobile}>🎿</Emoji> },
      { id: "squashracket", name: "Squash Racket", price: 90, icon: <Emoji isMobile={isMobile}>🎾</Emoji> },
      { id: "athleisure", name: "Athleisure Set", price: 90, icon: <Emoji isMobile={isMobile}>👟</Emoji> },
      { id: "tennis", name: "Tennis Racket", price: 100, icon: <Emoji isMobile={isMobile}>🎾</Emoji> },
      { id: "cricketpads", name: "Cricket Pads", price: 100, icon: <Emoji isMobile={isMobile}>🦵</Emoji> },
      { id: "poolcue", name: "Pool Cue", price: 100, icon: <Emoji isMobile={isMobile}>🎱</Emoji> },
      { id: "discus", name: "Discus", price: 100, icon: <Emoji isMobile={isMobile}>🥏</Emoji> },
      { id: "cricketbat", name: "Cricket Bat", price: 120, icon: <Emoji isMobile={isMobile}>🏏</Emoji> },
      { id: "tennisnet", name: "Tennis Net", price: 120, icon: <Emoji isMobile={isMobile}>🎾</Emoji> },
      { id: "highjump", name: "High Jump Bar", price: 120, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "catchermask", name: "Catcher's Mask", price: 120, icon: <Emoji isMobile={isMobile}>🎭</Emoji> },
      { id: "rollerblades", name: "Rollerblades", price: 120, icon: <Emoji isMobile={isMobile}>🛼</Emoji> },
      { id: "tracksuit", name: "Tracksuit", price: 120, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "shoes", name: "Running Shoes", price: 120, icon: <Emoji isMobile={isMobile}>👟</Emoji> },
      { id: "spikes", name: "Track Spikes", price: 120, icon: <Emoji isMobile={isMobile}>👟</Emoji> },
      { id: "skihelmet", name: "Ski Helmet", price: 120, icon: <Emoji isMobile={isMobile}>⛑️</Emoji> },

      // ADVANCED SPORTS EQUIPMENT ($150-$400)
      { id: "hammer", name: "Hammer Throw", price: 150, icon: <Emoji isMobile={isMobile}>🔨</Emoji> },
      { id: "dumbbell", name: "Dumbbells Set", price: 150, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "volleyballnet", name: "Volleyball Net", price: 150, icon: <Emoji isMobile={isMobile}>🏐</Emoji> },
      { id: "bases", name: "Baseball Bases Set", price: 150, icon: <Emoji isMobile={isMobile}>⚾</Emoji> },
      { id: "hockeymask", name: "Hockey Mask", price: 150, icon: <Emoji isMobile={isMobile}>🎭</Emoji> },
      { id: "crickethelmet", name: "Cricket Helmet", price: 150, icon: <Emoji isMobile={isMobile}>⛑️</Emoji> },
      { id: "cleats", name: "Soccer Cleats", price: 150, icon: <Emoji isMobile={isMobile}>👟</Emoji> },
      { id: "wetsuit", name: "Wetsuit", price: 150, icon: <Emoji isMobile={isMobile}>🏄</Emoji> },
      { id: "hurdles", name: "Running Hurdles", price: 180, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "putter", name: "Golf Putter", price: 180, icon: <Emoji isMobile={isMobile}>⛳</Emoji> },
      { id: "iceskates", name: "Ice Skates", price: 180, icon: <Emoji isMobile={isMobile}>⛸️</Emoji> },
      { id: "golfbag", name: "Golf Bag", price: 200, icon: <Emoji isMobile={isMobile}>⛳</Emoji> },
      { id: "javelin", name: "Javelin", price: 200, icon: <Emoji isMobile={isMobile}>🏹</Emoji> },
      { id: "boundaryrope", name: "Boundary Rope", price: 200, icon: <Emoji isMobile={isMobile}>🪢</Emoji> },
      { id: "hockeyskates", name: "Hockey Skates", price: 200, icon: <Emoji isMobile={isMobile}>⛸️</Emoji> },
      { id: "punchingbag", name: "Punching Bag", price: 200, icon: <Emoji isMobile={isMobile}>🥊</Emoji> },
      { id: "scoreboardportable", name: "Portable Scoreboard", price: 200, icon: <Emoji isMobile={isMobile}>📊</Emoji> },
      { id: "fitnesstracker", name: "Fitness Tracker", price: 200, icon: <Emoji isMobile={isMobile}>⌚</Emoji> },
      { id: "startingblocks", name: "Starting Blocks", price: 250, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "golf", name: "Golf Club", price: 250, icon: <Emoji isMobile={isMobile}>🏌️</Emoji> },
      { id: "barbell", name: "Barbell Set", price: 300, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "hockeypad", name: "Hockey Pads", price: 300, icon: <Emoji isMobile={isMobile}>🦵</Emoji> },
      { id: "polevault", name: "Pole Vault Pole", price: 300, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "surfboard", name: "Surfboard", price: 300, icon: <Emoji isMobile={isMobile}>🏄</Emoji> },
      { id: "wakeboard", name: "Wakeboard", price: 350, icon: <Emoji isMobile={isMobile}>🏄</Emoji> },
      { id: "bicycle", name: "Bicycle", price: 400, icon: <Emoji isMobile={isMobile}>🚲</Emoji> },
      { id: "hockeygoal", name: "Hockey Goal", price: 400, icon: <Emoji isMobile={isMobile}>🥅</Emoji> },
      { id: "waterski", name: "Water Skis", price: 400, icon: <Emoji isMobile={isMobile}>🎿</Emoji> },
      { id: "skiboots", name: "Ski Boots", price: 400, icon: <Emoji isMobile={isMobile}>🥾</Emoji> },

      // PREMIUM SPORTS EQUIPMENT ($500-$1200)
      { id: "snowboard", name: "Snowboard", price: 500, icon: <Emoji isMobile={isMobile}>🏂</Emoji> },
      { id: "basketballhoop", name: "Basketball Hoop", price: 500, icon: <Emoji isMobile={isMobile}>🏀</Emoji> },
      { id: "mats", name: "Wrestling Mats", price: 500, icon: <Emoji isMobile={isMobile}>🤸</Emoji> },
      { id: "skis", name: "Alpine Skis", price: 600, icon: <Emoji isMobile={isMobile}>🎿</Emoji> },
      { id: "stationary", name: "Stationary Bike", price: 600, icon: <Emoji isMobile={isMobile}>🚴</Emoji> },
      { id: "benchpress", name: "Bench Press", price: 800, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "goalpost", name: "Soccer Goal Post", price: 800, icon: <Emoji isMobile={isMobile}>🥅</Emoji> },
      { id: "treadmill", name: "Treadmill", price: 800, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "elliptical", name: "Elliptical Machine", price: 1200, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "ebike", name: "Electric Bike", price: 1500, icon: <Emoji isMobile={isMobile}>🚲</Emoji> },

      // LUXURY SPORTS ITEMS ($2500+)
      { id: "billiardtable", name: "Billiard Table", price: 2500, icon: <Emoji isMobile={isMobile}>🎱</Emoji> },
      { id: "golfcart", name: "Golf Cart", price: 8000, icon: <Emoji isMobile={isMobile}>🚗</Emoji> },
      { id: "boxingring", name: "Boxing Ring", price: 15000, icon: <Emoji isMobile={isMobile}>🥊</Emoji> },
      { id: "sportscourt", name: "Basketball Court", price: 80000, icon: <Emoji isMobile={isMobile}>🏀</Emoji> },
      { id: "tenniscourt", name: "Tennis Court", price: 120000, icon: <Emoji isMobile={isMobile}>🎾</Emoji> },
      { id: "bleachers", name: "Stadium Bleachers", price: 150000, icon: <Emoji isMobile={isMobile}>🪑</Emoji> },
      { id: "soccerpitch", name: "Soccer Pitch", price: 200000, icon: <Emoji isMobile={isMobile}>⚽</Emoji> },
      { id: "floodlights", name: "Stadium Floodlights", price: 300000, icon: <Emoji isMobile={isMobile}>💡</Emoji> },
      { id: "track", name: "Running Track", price: 400000, icon: <Emoji isMobile={isMobile}>🏃</Emoji> },
      { id: "scoreboard", name: "Digital Scoreboard", price: 500000, icon: <Emoji isMobile={isMobile}>📊</Emoji> },
      { id: "gymnasium", name: "Gymnasium", price: 1500000, icon: <Emoji isMobile={isMobile}>🏋️</Emoji> },
      { id: "swimmingpool", name: "Olympic Pool", price: 2000000, icon: <Emoji isMobile={isMobile}>🏊</Emoji> },
      { id: "sportscomplex", name: "Sports Complex", price: 80000000, icon: <Emoji isMobile={isMobile}>🏢</Emoji> },
      { id: "arena", name: "Indoor Arena", price: 150000000, icon: <Emoji isMobile={isMobile}>🏛️</Emoji> },
      { id: "stadium", name: "Sports Stadium", price: 250000000, icon: <Emoji isMobile={isMobile}>🏟️</Emoji> },
    ];

    // Sort all sports items by price (low to high)
    return sportsItems.sort((a, b) => a.price - b.price);
  }, [isMobile]);

  // Improved initialization functions with better error handling
  const getInitialMoney = () => {
    try {
      const savedMoney = localStorage.getItem("buysell_money");
      return savedMoney ? JSON.parse(savedMoney) : START;
    } catch (error) {
      console.error("Error loading money from localStorage:", error);
      return START;
    }
  };

  const getInitialBag = () => {
    try {
      const savedBag = localStorage.getItem("buysell_bag");
      const defaultBag = Object.fromEntries(items.map((i) => [i.id, 0]));

      if (savedBag) {
        const parsedBag = JSON.parse(savedBag);
        // Merge saved bag with default bag to ensure all items exist
        const mergedBag = { ...defaultBag, ...parsedBag };
        return mergedBag;
      }

      return defaultBag;
    } catch (error) {
      console.error("Error loading bag from localStorage:", error);
      return Object.fromEntries(items.map((i) => [i.id, 0]));
    }
  };

  const [money, setMoney] = useState(getInitialMoney);
  const [bag, setBag] = useState(() => {
    // Initialize bag state safely
    const initialBag = {};
    items.forEach((item) => {
      initialBag[item.id] = 0;
    });

    try {
      const savedBag = localStorage.getItem("buysell_bag");
      if (savedBag) {
        const parsedBag = JSON.parse(savedBag);
        // Merge with default values to ensure all items exist
        return { ...initialBag, ...parsedBag };
      }
    } catch (error) {
      console.error("Error loading bag from localStorage:", error);
    }

    return initialBag;
  });
  const [message, setMessage] = useState("");

  // Use animated money hook
  const { displayAmount: animatedMoney, isAnimating } = useAnimatedMoney(money, 800);

  // Save to localStorage whenever money or bag changes
  useEffect(() => {
    try {
      localStorage.setItem("buysell_money", JSON.stringify(money));
    } catch (error) {
      console.error("Error saving money to localStorage:", error);
    }
  }, [money]);

  useEffect(() => {
    try {
      localStorage.setItem("buysell_bag", JSON.stringify(bag));
    } catch (error) {
      console.error("Error saving bag to localStorage:", error);
    }
  }, [bag]);

  // Load saved data on component mount and update bag for new items
  useEffect(() => {
    try {
      const savedMoney = localStorage.getItem("buysell_money");
      const savedBag = localStorage.getItem("buysell_bag");

      if (savedMoney) {
        setMoney(JSON.parse(savedMoney));
      }

      if (savedBag) {
        const parsedBag = JSON.parse(savedBag);
        // Create a complete bag with all current items
        const completeBag = {};
        items.forEach((item) => {
          completeBag[item.id] = parsedBag[item.id] || 0;
        });
        setBag(completeBag);
      }
    } catch (error) {
      console.error("Error loading data from localStorage:", error);
    }
  }, [items]);

  const buy = (item) => {
    if (money < item.price) {
      setMessage(`Not enough balance to buy ${item.name}.`);
      return;
    }
    setMoney((m) => m - item.price);
    setBag((b) => ({ ...b, [item.id]: (b[item.id] || 0) + 1 }));
    setMessage("");
  };

  const sell = (item) => {
    const currentQty = bag[item.id] || 0;
    if (currentQty <= 0) return;
    setMoney((m) => m + item.price);
    setBag((b) => ({ ...b, [item.id]: currentQty - 1 }));
    setMessage("");
  };

  // New behavior: clamp quantity increases to the maximum affordable amount
  const updateQuantity = (itemId, newQty) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    const currentQty = bag[itemId] || 0;
    const normalizedNewQty = Math.max(0, Number.isFinite(newQty) ? Math.floor(newQty) : 0);

    if (normalizedNewQty > currentQty) {
      // Buying more items
      const desiredAdd = normalizedNewQty - currentQty;
      const price = item.price > 0 ? item.price : 0;

      if (price === 0) {
        // If price is zero, just set directly (safety)
        setBag((b) => ({ ...b, [itemId]: normalizedNewQty }));
        setMessage("");
        return;
      }

      const maxAffordableAdd = Math.floor(money / price);
      const actualAdd = Math.max(0, Math.min(desiredAdd, maxAffordableAdd));
      const finalQty = currentQty + actualAdd;

      if (actualAdd <= 0) {
        setMessage(`Not enough balance to buy more ${item.name}.`);
        return;
      }

      const totalCost = actualAdd * price;
      setMoney((m) => m - totalCost);
      setBag((b) => ({ ...b, [itemId]: finalQty }));

      // If we clamped, inform the user
      if (actualAdd < desiredAdd) {
        setMessage(`Adjusted to maximum affordable quantity: ${finalQty}.`);
      } else {
        setMessage("");
      }
    } else if (normalizedNewQty < currentQty) {
      // Selling items
      const diff = currentQty - normalizedNewQty;
      const refund = diff * item.price;
      setMoney((m) => m + refund);
      setBag((b) => ({ ...b, [itemId]: normalizedNewQty }));
      setMessage("");
    }
    // If equal, do nothing
  };

  // Reset function to clear localStorage and restart
  const resetGame = () => {
    try {
      localStorage.removeItem("buysell_money");
      localStorage.removeItem("buysell_bag");
      setMoney(START);
      const resetBag = {};
      items.forEach((item) => {
        resetBag[item.id] = 0;
      });
      setBag(resetBag);
      setMessage("Game reset! Starting with fresh money and empty cart.");
    } catch (error) {
      console.error("Error resetting game:", error);
    }
  };

  // Calculate receipt items with safe bag access
  const receiptItems = items.filter((item) => (bag[item.id] || 0) > 0);
  const totalCost = receiptItems.reduce(
    (total, item) => total + item.price * (bag[item.id] || 0),
    0
  );

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      {/* Add custom styles for xs breakpoint and w-18/h-18 classes */}
      <style jsx>{`
        @media (min-width: 475px) {
          .xs\\:w-12 { width: 3rem; }
          .xs\\:h-12 { height: 3rem; }
          .xs\\:w-14 { width: 3.5rem; }
          .xs\\:h-14 { height: 3.5rem; }
          .xs\\:text-3xl { font-size: 1.875rem; line-height: 2.25rem; }
          .xs\\:text-4xl { font-size: 2.25rem; line-height: 2.5rem; }
          .xs\\:text-sm { font-size: 0.875rem; line-height: 1.25rem; }
          .xs\\:text-xs { font-size: 0.75rem; line-height: 1rem; }
          .xs\\:px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
          .xs\\:py-1\\.5 { padding-top: 0.375rem; padding-bottom: 0.375rem; }
          .xs\\:w-10 { width: 2.5rem; }
          .xs\\:h-10 { height: 2.5rem; }
          .xs\\:p-3 { padding: 0.75rem; }
          .xs\\:mb-2 { margin-bottom: 0.5rem; }
          .xs\\:mb-3 { margin-bottom: 0.75rem; }
          .xs\\:gap-1 { gap: 0.25rem; }
          .xs\\:min-h-\\[36px\\] { min-height: 36px; }
          .xs\\:min-h-\\[220px\\] { min-height: 220px; }
          .xs\\:rounded-xl { border-radius: 0.75rem; }
          .xs\\:w-8 { width: 2rem; }
          .xs\\:h-8 { height: 2rem; }
        }
        .sm\\:w-18 { width: 4.5rem; }
        .sm\\:h-18 { height: 4.5rem; }
        .sm\\:text-5xl { font-size: 3rem; line-height: 1; }
        .sm\\:w-14 { width: 3.5rem; }
        .sm\\:h-14 { height: 3.5rem; }
      `}</style>

      {/* Static Title Header */}
      <div className="w-full bg-white py-6 md:py-8 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-center text-gray-700 font-bold text-2xl md:text-3xl lg:text-4xl">
            Spend Your Sports Money
          </h1>
        </div>
      </div>

      {/* Sticky Money Banner */}
      <div className="sticky top-0 z-50 w-full bg-emerald-500 shadow-lg">
        <div className={`max-w-6xl mx-auto ${isMobile ? "px-2 xs:px-3 sm:px-4 py-3 xs:py-4 sm:py-5" : "px-4 py-5"}`}>
          <div className="text-center text-white">
            <div className={`font-extrabold tracking-tight transition-all duration-200 ${isAnimating ? "scale-105" : "scale-100"} ${isMobile ? "text-xl xs:text-2xl sm:text-3xl" : "text-4xl lg:text-5xl"}`}>
              {moneyFormat(animatedMoney)}
            </div>
          </div>
        </div>
      </div>

      <div className={`max-w-6xl mx-auto ${isMobile ? "px-2 xs:px-3 sm:px-4 py-3 xs:py-4 sm:py-6" : "px-4 sm:px-6 py-8"}`}>
        {message && (
          <div className={`mb-${isMobile ? "3 xs:mb-4 sm:mb-6" : "6"} px-${isMobile ? "3 xs:px-3 sm:px-4" : "4"} py-${isMobile ? "2 xs:py-2 sm:py-3" : "3"} bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-lg ${isMobile ? "text-xs xs:text-sm sm:text-base" : "text-base"}`}>
            {message}
          </div>
        )}

        {/* Reset Button */}
        <div className={`mb-${isMobile ? "3 xs:mb-4 sm:mb-6" : "6"} text-center`}>
          <button
            onClick={resetGame}
            className={`px-${isMobile ? "3 xs:px-4 sm:px-4" : "4"} py-${isMobile ? "1.5 xs:py-2 sm:py-2" : "2"} rounded-md bg-red-500 text-white font-semibold hover:bg-red-600 active:scale-[0.${isMobile ? "95" : "98"}] transition${isMobile ? "-all duration-150" : ""} ${isMobile ? "text-xs xs:text-sm sm:text-base min-h-[36px] xs:min-h-[40px] sm:min-h-[44px]" : "text-base"}`}
          >
            Reset Game
          </button>
        </div>

        {/* Grid layout */}
        <div className={`grid ${isMobile ? "grid-cols-2 gap-1.5 xs:gap-2 sm:gap-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"}`}>
          {items.map((it) => (
            <Card
              key={it.id}
              name={it.name}
              price={it.price}
              qty={bag[it.id] || 0} // Safe access with fallback
              onBuy={() => buy(it)}
              onSell={() => sell(it)}
              onQtyChange={(newQty) => updateQuantity(it.id, newQty)}
              icon={it.icon}
              canAfford={money >= it.price}
              isMobile={isMobile}
            />
          ))}
        </div>

        {/* Receipt Section */}
        {receiptItems.length > 0 && (
          <div className={`mt-${isMobile ? "6 xs:mt-8 sm:mt-12" : "16"} bg-white rounded-${isMobile ? "lg xs:rounded-xl sm:rounded-xl" : "2xl"} border border-gray-200 shadow-lg p-${isMobile ? "3 xs:p-4 sm:p-6" : "8"}`}>
            <h2 className={`${isMobile ? "text-lg xs:text-xl sm:text-2xl" : "text-3xl"} font-bold text-gray-900 text-center mb-${isMobile ? "3 xs:mb-4 sm:mb-6" : "8"}`}>Your Receipt</h2>

            <div className={`space-y-${isMobile ? "1.5 xs:space-y-2 sm:space-y-3" : "4"} mb-${isMobile ? "3 xs:mb-4 sm:mb-5" : "6"}`}>
              {receiptItems.map((item) => (
                <div key={item.id} className={`flex justify-between items-center py-${isMobile ? "1 xs:py-1 sm:py-1.5" : "2"}`}>
                  <div className={`flex items-center gap-${isMobile ? "2 xs:gap-2 sm:gap-3" : "4"}`}>
                    <span className={`${isMobile ? "text-xs xs:text-sm sm:text-base" : "text-lg"} font-medium text-gray-900`}>{item.name}</span>
                    <span className={`${isMobile ? "text-xs xs:text-xs sm:text-sm" : "text-base"} text-gray-600`}>x{bag[item.id] || 0}</span>
                  </div>
                  <span className={`${isMobile ? "text-xs xs:text-sm sm:text-base" : "text-lg"} font-semibold text-teal-600`}>
                    {moneyFormat(item.price * (bag[item.id] || 0))}
                  </span>
                </div>
              ))}
            </div>

            <div className={`border-t-2 border-gray-300 pt-${isMobile ? "2 xs:pt-3 sm:pt-3" : "4"}`}>
              <div className="flex justify-between items-center">
                <span className={`${isMobile ? "text-base xs:text-lg sm:text-xl" : "text-2xl"} font-bold text-gray-900`}>TOTAL</span>
                <span className={`${isMobile ? "text-base xs:text-lg sm:text-xl" : "text-2xl"} font-bold text-teal-600`}>
                  {moneyFormat(totalCost)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={`mt-${isMobile ? "4 xs:mt-6 sm:mt-8" : "10"}`}>
         <Link
  to="/"
  className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all"
>
  <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
  <span className="sm:hidden">Back</span>
  <span className="hidden sm:inline">Back to Home</span>
</Link>

        </div>
      </div>
    </div>
  );
}
